import { type ReactNode } from "react";

// The lookahead stops a match before it would swallow the next URL when two
// URLs sit back-to-back with no whitespace between them (e.g. "a,https://b").
const NOT_NEXT_URL = "(?:(?!https?:\\/\\/|www\\.)\\S)";
const URL_PATTERN = new RegExp(
  `(https?:\\/\\/(?:www\\.)?${NOT_NEXT_URL}+|www\\.${NOT_NEXT_URL}+)`,
  "g"
);
// Trailing sentence/bracket punctuation is almost never part of the URL
// itself (e.g. "see https://example.com." or "(https://example.com)").
// A char-set loop instead of a regex avoids any backtracking concerns.
const TRAILING_PUNCTUATION_CHARS = new Set([
  ".",
  ",",
  ";",
  ":",
  "!",
  "?",
  ")",
  "]",
  "}",
  "'",
  '"'
]);

const trimTrailingPunctuation = (token: string): string => {
  let end = token.length;
  while (end > 0 && TRAILING_PUNCTUATION_CHARS.has(token[end - 1])) end--;
  return token.slice(0, end);
};

const LINK_CLASS = "text-primary-500 underline underline-offset-2 break-words";

const toHref = (token: string) =>
  token.startsWith("http") ? token : `https://${token}`;

// Long raw URLs overwhelm the surrounding text, so the link shows just the
// host and reveals the full URL on hover via the native title tooltip.
const toDisplayLabel = (token: string, href: string): string => {
  try {
    return new URL(href).hostname;
  } catch {
    return token;
  }
};

export const LinkifiedText = ({
  text,
  showFullUrl = false
}: {
  text: string;
  showFullUrl?: boolean;
}): ReactNode => {
  const matches = [...text.matchAll(URL_PATTERN)];
  if (!matches.length) return text;

  const nodes: ReactNode[] = [];
  let cursor = 0;

  matches.forEach((match, index) => {
    const token = trimTrailingPunctuation(match[0]);
    const start = match.index ?? 0;
    if (start > cursor) nodes.push(text.slice(cursor, start));

    const href = toHref(token);
    nodes.push(
      <a
        key={`${start}-${index}`}
        href={href}
        title={href}
        target="_blank"
        rel="noopener noreferrer"
        className={LINK_CLASS}
      >
        {showFullUrl ? token : toDisplayLabel(token, href)}
      </a>
    );
    cursor = start + token.length;
  });

  if (cursor < text.length) nodes.push(text.slice(cursor));

  return nodes;
};
